import { cn } from '@/lib/utils/cn';

export interface PasswordStrengthMeterProps {
  /** Score 0 (empty/too weak) to 4 (excellent). */
  score: number;
  /** Optional override for the label (e.g. translated string). */
  label?: string;
  className?: string;
}

const SEGMENT_COUNT = 4;

const SCORE_TO_LABEL: Record<number, string> = {
  0: 'Too weak',
  1: 'Weak',
  2: 'Fair',
  3: 'Strong',
  4: 'Excellent',
};

const SCORE_TO_COLOR: Record<number, string> = {
  0: 'bg-muted',
  1: 'bg-destructive',
  2: 'bg-orange-500',
  3: 'bg-yellow-500',
  4: 'bg-green-500',
};

const SCORE_TO_TEXT_COLOR: Record<number, string> = {
  0: 'text-muted-foreground',
  1: 'text-destructive',
  2: 'text-orange-600 dark:text-orange-500',
  3: 'text-yellow-700 dark:text-yellow-500',
  4: 'text-green-600 dark:text-green-500',
};

export function PasswordStrengthMeter({ score, label, className }: PasswordStrengthMeterProps) {
  const clamped = Math.max(0, Math.min(4, score));
  const displayLabel = label ?? SCORE_TO_LABEL[clamped];
  const segmentColor = SCORE_TO_COLOR[clamped];
  const textColor = SCORE_TO_TEXT_COLOR[clamped];

  return (
    <div
      className={cn('space-y-1.5', className)}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={SEGMENT_COUNT}
      aria-valuenow={clamped}
      aria-valuetext={displayLabel}
    >
      <div className="flex gap-1" aria-hidden="true">
        {Array.from({ length: SEGMENT_COUNT }, (_, i) => {
          const filled = i < clamped;
          return (
            <div
              key={i}
              className={cn(
                'h-1.5 flex-1 rounded-full transition-colors',
                filled ? segmentColor : 'bg-muted'
              )}
            />
          );
        })}
      </div>
      <div className={cn('text-xs font-medium', textColor)}>{displayLabel}</div>
    </div>
  );
}
